import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  MoreHorizontal,
  Pin,
  PanelLeft,
  MessagesSquare,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/shared/components/ui/dropdown-menu';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import { getInitials } from '@/core/lib/get-initials';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useArchiveTask, useUnarchiveTask } from '../../hooks/useTaskArchiveMutations';
import { CopyTaskDialog } from './CopyTaskDialog';
import type { TaskDetail } from '../../types';

interface TaskDialogHeaderProps {
  task: TaskDetail;
  sidebarOpen: boolean;
  activityPanelOpen: boolean;
  onToggleSidebar: () => void;
  onToggleActivityPanel: () => void;
  onClose: () => void;
}

export function TaskDialogHeader({
  task,
  sidebarOpen,
  activityPanelOpen,
  onToggleSidebar,
  onToggleActivityPanel,
  onClose,
}: TaskDialogHeaderProps) {
  const { t } = useTranslation('tasks');
  const [copyOpen, setCopyOpen] = useState(false);
  const { mutate: archiveTask } = useArchiveTask();
  const { mutate: unarchiveTask } = useUnarchiveTask();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const formatInstant = useInstantFormat();

  const createdDate = task.createdAt ? formatInstant(task.createdAt) : '';

  async function handleArchive() {
    const result = await confirm({
      title: t('archive.confirmTitle'),
      description: t('archive.confirmDesc'),
      confirmLabel: t('archive.confirmAction'),
      variant: 'destructive',
    });
    if (result.confirmed) archiveTask(task.id);
  }

  function handleUnarchive() {
    unarchiveTask(task.id);
  }

  return (
    <>
      <div
        className="flex items-center gap-1 border-b px-4 py-1.5"
        data-testid="tasks-dialog-header"
      >
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onToggleSidebar}
                aria-label={t('dialog.sidebarToggleAria')}
                aria-expanded={sidebarOpen}
                data-testid="tasks-dialog-sidebar-toggle"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('dialog.sidebarToggleAria')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={t('dialog.goUpAria')}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('dialog.goUpAria')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={t('dialog.goDownAria')}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('dialog.goDownAria')}</TooltipContent>
          </Tooltip>
        </div>

        <div className="ml-auto" />

        <div className="flex items-center gap-1">
          {task.createdBy && (
            <div className="mr-2 flex items-center gap-1.5">
              <Avatar className="h-6 w-6">
                {task.createdBy.avatarUrl && (
                  <AvatarImage src={task.createdBy.avatarUrl} alt={task.createdBy.name} />
                )}
                <AvatarFallback className="text-[10px]">
                  {getInitials(task.createdBy.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs text-muted-foreground">{task.createdBy.name}</span>
              <span className="text-xs text-muted-foreground">{createdDate}</span>
            </div>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={t('dialog.pinAria')}
                data-testid="tasks-dialog-pin-btn"
              >
                <Pin className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('dialog.pinAria')}</TooltipContent>
          </Tooltip>

          {/* modal={false}: DropdownMenu defaults to modal, which fights the parent
              Dialog's own modal/focus-trap — an outside click that only dismisses this
              menu also gets read as an outside click on the Dialog, closing it too. */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label={t('dialog.moreOptionsAria')}
                data-testid="tasks-dialog-more-btn"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => setCopyOpen(true)}
                data-testid="tasks-dialog-duplicate-btn"
              >
                {t('dialog.duplicate')}
              </DropdownMenuItem>
              {task.isArchived ? (
                <DropdownMenuItem
                  onClick={handleUnarchive}
                  data-testid="tasks-dialog-unarchive-btn"
                >
                  {t('dialog.unarchive')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleArchive} data-testid="tasks-dialog-archive-btn">
                  {t('dialog.archive')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onToggleActivityPanel}
                aria-label={t('activity.togglePanel')}
                aria-expanded={activityPanelOpen}
                data-testid="tasks-activity-panel-toggle"
              >
                <MessagesSquare className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('activity.togglePanel')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onClose}
                aria-label={t('dialog.closeAria')}
                data-testid="tasks-dialog-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('dialog.closeAria')}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <CopyTaskDialog taskId={task.id} open={copyOpen} onOpenChange={setCopyOpen} />

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
